import { selectAnalysis } from "@/store/point-cloud-analysis-tool-selector";
import {
  addAnalysis,
  PointCloudAnalysis,
  PolygonPointCloudAnalysis,
  CadPointCloudAnalysis,
  ReferencePlaneType,
  PointCloudAnalysisLabel,
  PointCloudAnalysisPlane,
  PointCloudVolumeAnalysisResult,
} from "@/store/point-cloud-analysis-tool-slice";
import { AppDispatch, RootState } from "@/store/store";
import { ANALYSIS_SEARCH_DISTANCE_DEFAULT } from "@/utils/colormap-analysis-utils";
import { selectIElementWorldMatrix4 } from "@/utils/transform-conversion-parsed";
import {
  GUID,
  PropOptional,
  PropRequired,
  robustFetch,
  validateArrayOf,
  validateNotNullishObject,
  validateOfType,
  validatePrimitive,
} from "@faro-lotv/foundation";
import { IElementAnalysis } from "@faro-lotv/ielement-types";
import { createSignedUrlForIElementUri } from "@faro-lotv/project-source";
import { ProjectApi } from "@faro-lotv/service-wires";
import { Box3, Matrix4, Quaternion, Vector3, Vector3Tuple } from "three";
import { isPlainObject } from "es-toolkit";
import { isClashRun, type ClashRun } from "./cad-analysis/cad-clash-detection";
import { isCadDeviationColouring } from "./cad-analysis/cad-deviation-colouring";

/**
 * The point cloud color map analysis setting that will be saved in json file
 *
 * The difference between ColorMapAnalysisData and PointCloudAnalysis is as follows:
 * 1. id in PointCloudAnalysis will be used as analysis IElement id, no need to save it in json file
 * 2. everything in PointCloudAnalysis is in world coordinate system while in ColorMapAnalysisData,
 *    they are all in local coordinate relative to analysis IElement
 * 3. `searchDistance` is a new parameter, not presented in json saved before, a default value
 *     will be used when loading old json data.
 */
type ColorMapAnalysisData = Omit<
  PolygonPointCloudAnalysis,
  "id" | "isVisible" | "isDirty" | "searchDistance" | "fittedPlanesMap"
> & { searchDistance?: number };

type CadAnalysisData = Omit<
  CadPointCloudAnalysis,
  "id" | "isVisible" | "isDirty"
> & { version: 2 | 3 | 4 };

/**
 * convert given PointCloudAnalysis into ColorMapAnalysisData and create the json file
 *
 * @param analysis the given PointCloudAnalysis object
 * @param worldTransform the world matrix of IElementAnalysis
 * @returns json file of the color map analysis data in local coordinate space
 */
export function createAnalysisJsonFile(
  analysis: PointCloudAnalysis,
  worldTransform: Matrix4,
): File {
  const colorMapAnalysisData = transformPointCloudAnalysisObject(
    analysis,
    worldTransform,
  );

  const jsonData = JSON.stringify(colorMapAnalysisData);

  return new File([jsonData], "analysis.json", {
    type: "application/json",
  });
}

/**
 * @param iElements the given analysis ielement array
 * @param appState The app state
 * @param dispatch the dispatch function to update the current state
 * @param projectApi ProjectApi client to sign download urls with
 */
export function loadAnalysis(
  iElements: IElementAnalysis[],
  appState: RootState,
  dispatch: AppDispatch,
  projectApi: ProjectApi,
): void {
  iElements.forEach(async (iElement) => {
    // If the analysis is not in the store yet, fetch the json file and add the analysis in the store.
    // Do nothing if the analysis already exist in the store.
    if (selectAnalysis(iElement.id)(appState) === undefined) {
      const signedUrl = await createSignedUrlForIElementUri(
        projectApi,
        iElement,
      ).requestSignedUrl();

      const res = await robustFetch(signedUrl);
      if (!res.ok) {
        console.error(res.statusText);
        throw new Error("Error while fetching analysis");
      }
      const analysisData = await res.json();

      const analysis = parseAnalysisData(
        iElement.id,
        analysisData,
        selectIElementWorldMatrix4(iElement.id)(appState),
      );
      if (analysis) {
        dispatch(addAnalysis({ pointCloudID: analysis.parentId, analysis }));
      }
    }
  });
}

/**
 * @param id Saved analysis element ID.
 * @param data Untrusted JSON from the analysis file.
 * @param worldMatrix Analysis container pose.
 * @returns A supported analysis, preserving legacy polygon files and rejecting malformed CAD references.
 */
export function parseAnalysisData(
  id: GUID,
  data: unknown,
  worldMatrix: Matrix4,
): PointCloudAnalysis | undefined {
  if (isCadAnalysisData(data)) {
    return {
      ...data,
      id,
      isVisible: true,
      isDirty: false,
      bounds: transformBounds(data.bounds, worldMatrix),
      labels: getTransformedData(worldMatrix, [], data.labels).labels,
      clashes: transformClashes(data.clashes, worldMatrix),
      previousClashes: transformClashes(data.previousClashes, worldMatrix),
    };
  }
  if (isPlainObject(data) && isColorMapAnalysisData(data)) {
    return transformColorMapAnalysisData(id, data, worldMatrix);
  }
}

/**
 * transform a given PointCloudAnalysis object from world space to local space and construct
 * ColorMapAnalysisData object to be save in json file
 *
 * @param analysis the PointCloudAnalysis object to transform
 * @param worldTransform the world matrix used to transform the PointCloudAnalysis object
 * @returns the ColorMapAnalysisData object to be saved in json file in local coordinate space
 */
function transformPointCloudAnalysisObject(
  analysis: PointCloudAnalysis,
  worldTransform: Matrix4,
): ColorMapAnalysisData | CadAnalysisData {
  const matrix = worldTransform.clone().invert();
  if (analysis.kind === "cad") {
    const legacyVersion = analysis.clashes ? 3 : 2;
    return {
      version: analysis.deviationColouring ? 4 : legacyVersion,
      deviationColouring: analysis.deviationColouring,
      kind: "cad",
      referencePlaneType: ReferencePlaneType.cad,
      parentId: analysis.parentId,
      tolerance: analysis.tolerance,
      searchDistance: analysis.searchDistance,
      colormap: analysis.colormap,
      cadReference: analysis.cadReference,
      clashClearance: analysis.clashClearance,
      clashMergeDistance: analysis.clashMergeDistance,
      clashes: transformClashes(analysis.clashes, matrix),
      previousClashes: transformClashes(analysis.previousClashes, matrix),
      bounds: transformBounds(analysis.bounds, matrix),
      labels: getTransformedData(matrix, [], analysis.labels).labels,
    };
  }

  const transformedData = getTransformedData(
    matrix,
    analysis.polygonSelection,
    analysis.labels,
    analysis.fittedPlane,
    analysis.fittedPlumbPlane,
    analysis.volumeAnalysisResult,
  );

  return {
    polygonSelection: transformedData.polygon,
    tolerance: analysis.tolerance,
    searchDistance: analysis.searchDistance,
    parentId: analysis.parentId,
    referencePlaneType: analysis.referencePlaneType,
    elevation: analysis.elevation,
    showReferencePlane: analysis.showReferencePlane,
    colormap: analysis.colormap,
    fittedPlane: transformedData.fittedPlane,
    fittedPlumbPlane: transformedData.fittedPlumbPlane,
    labels: transformedData.labels,
    volumeBasePlaneType: analysis.volumeBasePlaneType,
    volumeBasePlaneElevation: analysis.volumeBasePlaneElevation,
    volumeDisplayNbIsoLines: analysis.volumeDisplayNbIsoLines,
    volumeDisplayOffset: analysis.volumeDisplayOffset,
    volumeAnalysisResult: transformedData.volumeAnalysisResult,
  };
}

/**
 * transform a given ColorMapAnalysisData object from local space to world space and construct
 * PointCloudAnalysis object to be used in app
 *
 * @param id the GUID of the PointCloudAnalysis object
 * @param analysisData the given ColorMapAnalysisData object fetched from json file
 * @param worldTransform the world matrix used to transform the ColorMapAnalysisData object
 * @returns the PointCloudAnalysis object in world space
 */
function transformColorMapAnalysisData(
  id: GUID,
  analysisData: ColorMapAnalysisData,
  worldTransform: Matrix4,
): PointCloudAnalysis {
  const transformedData = getTransformedData(
    worldTransform,
    analysisData.polygonSelection,
    analysisData.labels,
    analysisData.fittedPlane,
    analysisData.fittedPlumbPlane,
    analysisData.volumeAnalysisResult,
  );

  return {
    ...analysisData,
    searchDistance:
      analysisData.searchDistance ?? ANALYSIS_SEARCH_DISTANCE_DEFAULT,
    polygonSelection: transformedData.polygon,
    fittedPlane: transformedData.fittedPlane,
    fittedPlumbPlane: transformedData.fittedPlumbPlane,
    fittedPlanesMap: {},
    isDirty: false,
    isVisible: true,
    id,
    labels: transformedData.labels,
    volumeAnalysisResult: transformedData.volumeAnalysisResult,
  };
}

type TransformedResult = {
  polygon: Vector3Tuple[];
  fittedPlane?: PointCloudAnalysisPlane;
  fittedPlumbPlane?: PointCloudAnalysisPlane;
  labels: PointCloudAnalysisLabel[];
  volumeAnalysisResult?: PointCloudVolumeAnalysisResult;
};

/**
 * @param matrix transformation matrix to apply
 * @param polygon the given polygon to apply the given transformation matrix
 * @param labels the given deviation labels to apply the given transformation matrix
 * @param fittedPlane the optional fitted plane to apply the given transformation matrix
 * @param fittedPlumbPlane the optional fitted plumb plane to apply the given transformation matrix
 * @param volumeAnalysisResult the optional volume analysis result to apply the given transformation matrix
 * @returns the transformed result after applying transform matrix to the given data
 */
export function getTransformedData(
  matrix: Matrix4,
  polygon: Vector3Tuple[],
  labels: PointCloudAnalysisLabel[],
  fittedPlane?: PointCloudAnalysisPlane,
  fittedPlumbPlane?: PointCloudAnalysisPlane,
  volumeAnalysisResult?: PointCloudVolumeAnalysisResult,
): TransformedResult {
  const TEMP_VEC1 = new Vector3();
  const TEMP_VEC2 = new Vector3();
  const rotation = new Quaternion();
  matrix.decompose(TEMP_VEC1, rotation, TEMP_VEC2);

  const transformedPolygon = polygon.map((p) =>
    TEMP_VEC1.fromArray(p).applyMatrix4(matrix).toArray(),
  );

  const transformedFittedPlane = fittedPlane
    ? {
        normal: TEMP_VEC1.fromArray(fittedPlane.normal)
          .applyQuaternion(rotation)
          .toArray(),
        point: TEMP_VEC1.fromArray(fittedPlane.point)
          .applyMatrix4(matrix)
          .toArray(),
      }
    : undefined;

  const transformedFittedPlumbPlane = fittedPlumbPlane
    ? {
        normal: TEMP_VEC1.fromArray(fittedPlumbPlane.normal)
          .applyQuaternion(rotation)
          .toArray(),
        point: TEMP_VEC1.fromArray(fittedPlumbPlane.point)
          .applyMatrix4(matrix)
          .toArray(),
      }
    : undefined;

  const transformedLabels = labels.map((label) => ({
    ...label,
    position: TEMP_VEC1.fromArray(label.position)
      .applyMatrix4(matrix)
      .toArray(),
  }));

  const transformedVolumeAnalysisResult = volumeAnalysisResult
    ? {
        ...volumeAnalysisResult,
        basePlaneOrigin: TEMP_VEC1.fromArray(
          volumeAnalysisResult.basePlaneOrigin,
        )
          .applyMatrix4(matrix)
          .toArray(),
        basePlaneNormal: TEMP_VEC1.fromArray(
          volumeAnalysisResult.basePlaneNormal,
        )
          .applyQuaternion(rotation)
          .toArray(),
      }
    : undefined;

  return {
    polygon: transformedPolygon,
    fittedPlane: transformedFittedPlane,
    fittedPlumbPlane: transformedFittedPlumbPlane,
    labels: transformedLabels,
    volumeAnalysisResult: transformedVolumeAnalysisResult,
  };
}

/**
 * ColorMapAnalysisData type guard
 *
 * @param data The generic input record
 * @returns true if the generic input record describes ColorMapAnalysisData object
 */
function isColorMapAnalysisData(
  data: Record<string, unknown>,
): data is ColorMapAnalysisData {
  return (
    data.kind !== "cad" &&
    data.referencePlaneType !== ReferencePlaneType.cad &&
    validateArrayOf({
      object: data,
      prop: "polygonSelection",
      elementGuard: isVector3Tuple,
    }) &&
    validatePrimitive(data, "tolerance", "number", PropRequired) &&
    validatePrimitive(data, "searchDistance", "number", PropOptional) &&
    validatePrimitive(data, "parentId", "string", PropRequired) &&
    validatePrimitive(data, "elevation", "number", PropRequired) &&
    validatePrimitive(data, "referencePlaneType", "string", PropRequired) &&
    validatePrimitive(data, "showReferencePlane", "boolean", PropOptional) &&
    validateArrayOf({
      object: data,
      prop: "colormap",
      elementGuard: (element) =>
        validatePrimitive(element, "value", "number", PropRequired) &&
        validatePrimitive(element, "color", "string", PropRequired),
    }) &&
    validateOfType(
      data,
      "fittedPlane",
      isPointCloudAnalysisPlane,
      PropOptional,
    ) &&
    validateOfType(
      data,
      "fittedPlumbPlane",
      isPointCloudAnalysisPlane,
      PropOptional,
    ) &&
    validateArrayOf({
      object: data,
      prop: "labels",
      elementGuard: (element) =>
        validatePrimitive(element, "id", "string", PropRequired) &&
        validateNotNullishObject(element, "position") &&
        validateOfType(element, "position", isVector3Tuple, PropRequired),
    }) &&
    validateOfType(
      data,
      "volumeAnalysisResult",
      isPointCloudVolumeAnalysisResult,
      PropOptional,
    )
  );
}

function transformBounds(
  bounds: CadPointCloudAnalysis["bounds"],
  matrix: Matrix4,
): CadPointCloudAnalysis["bounds"] {
  const box = new Box3(
    new Vector3().fromArray(bounds[0]),
    new Vector3().fromArray(bounds[1]),
  ).applyMatrix4(matrix);
  return [box.min.toArray(), box.max.toArray()];
}

function transformClashes(
  run: ClashRun | undefined,
  matrix: Matrix4,
): ClashRun | undefined {
  return (
    run && {
      ...run,
      boundsFrame: matrix
        .clone()
        .multiply(
          new Matrix4().fromArray(run.boundsFrame ?? new Matrix4().toArray()),
        )
        .toArray(),
      processing: run.processing && {
        ...run.processing,
        frame: matrix
          .clone()
          .multiply(
            new Matrix4().fromArray(
              run.processing.frame ?? new Matrix4().toArray(),
            ),
          )
          .toArray(),
      },
      candidates: run.candidates.map((candidate) => ({
        ...candidate,
        position: new Vector3()
          .fromArray(candidate.position)
          .applyMatrix4(matrix)
          .toArray(),
        samples: candidate.samples?.map((point) =>
          new Vector3().fromArray(point).applyMatrix4(matrix).toArray(),
        ),
        witness: candidate.witness && {
          scan: new Vector3()
            .fromArray(candidate.witness.scan)
            .applyMatrix4(matrix)
            .toArray(),
          cad: new Vector3()
            .fromArray(candidate.witness.cad)
            .applyMatrix4(matrix)
            .toArray(),
        },
      })),
    }
  );
}

function isCadAnalysisData(data: unknown): data is CadAnalysisData {
  if (
    !isPlainObject(data) ||
    data.kind !== "cad" ||
    (data.version !== 2 && data.version !== 3 && data.version !== 4) ||
    data.referencePlaneType !== ReferencePlaneType.cad
  ) {
    return false;
  }
  const reference = data.cadReference;
  function finiteTuple(value: unknown, count: number): boolean {
    return (
      Array.isArray(value) &&
      value.length === count &&
      value.every(Number.isFinite)
    );
  }
  function validClipBox(value: unknown): boolean {
    if (value === undefined) return true;
    if (
      !isPlainObject(value) ||
      !finiteTuple(value.position, 3) ||
      !finiteTuple(value.size, 3) ||
      !finiteTuple(value.rotation, 4)
    ) {
      return false;
    }
    if (!Array.isArray(value.size) || !Array.isArray(value.rotation)) {
      return false;
    }
    const rotationTolerance = 0.0001;
    return (
      value.size.every((size) => size > 0) &&
      Math.abs(
        value.rotation.reduce(
          (sum, component) => sum + component * component,
          0,
        ) - 1,
      ) < rotationTolerance
    );
  }
  return (
    (data.clashes === undefined ||
      ((data.version === 3 || data.version === 4) &&
        isClashRun(data.clashes))) &&
    (data.previousClashes === undefined ||
      ((data.version === 3 || data.version === 4) &&
        isClashRun(data.previousClashes))) &&
    (data.clashClearance === undefined ||
      (typeof data.clashClearance === "number" &&
        Number.isFinite(data.clashClearance) &&
        data.clashClearance >= 0)) &&
    (data.clashMergeDistance === undefined ||
      (typeof data.clashMergeDistance === "number" &&
        Number.isFinite(data.clashMergeDistance) &&
        data.clashMergeDistance >= 0)) &&
    typeof data.parentId === "string" &&
    typeof data.tolerance === "number" &&
    Number.isFinite(data.tolerance) &&
    data.tolerance >= 0 &&
    typeof data.searchDistance === "number" &&
    Number.isFinite(data.searchDistance) &&
    data.searchDistance >= data.tolerance &&
    (data.version === 4
      ? isCadDeviationColouring(
          data.deviationColouring,
          data.tolerance,
          data.searchDistance,
        )
      : data.deviationColouring === undefined) &&
    Array.isArray(data.bounds) &&
    data.bounds.length === 2 &&
    data.bounds.every((point) => finiteTuple(point, 3)) &&
    Array.isArray(data.labels) &&
    data.labels.every(
      (label) =>
        isPlainObject(label) &&
        typeof label.id === "string" &&
        finiteTuple(label.position, 3),
    ) &&
    Array.isArray(data.colormap) &&
    data.colormap.length >= 2 &&
    data.colormap.every(
      (stop) =>
        isPlainObject(stop) &&
        typeof stop.value === "number" &&
        Number.isFinite(stop.value) &&
        typeof stop.color === "string",
    ) &&
    isPlainObject(reference) &&
    validClipBox(reference.clipBox) &&
    typeof reference.cadId === "string" &&
    typeof reference.name === "string" &&
    typeof reference.revision === "string" &&
    Number.isInteger(reference.objectId) &&
    typeof reference.all === "boolean" &&
    Array.isArray(reference.objectIds) &&
    reference.objectIds.every(Number.isInteger) &&
    finiteTuple(reference.cadTransform, 16) &&
    finiteTuple(reference.cloudTransform, 16)
  );
}

function isVector3Tuple(obj: unknown): obj is Vector3Tuple {
  if (!Array.isArray(obj) || obj.length !== 3) return false;

  return obj.every((child) => typeof child === "number");
}

function isPointCloudAnalysisPlane(
  data: unknown,
): data is PointCloudAnalysisPlane {
  return (
    validateNotNullishObject(data, "PointCloudAnalysisPlane") &&
    validateOfType(data, "normal", isVector3Tuple, PropOptional) &&
    validateOfType(data, "point", isVector3Tuple, PropOptional)
  );
}

function isPointCloudVolumeAnalysisResult(
  data: unknown,
): data is PointCloudVolumeAnalysisResult {
  return (
    validateNotNullishObject(data, "PointCloudVolumeAnalysisResult") &&
    validateOfType(data, "basePlaneOrigin", isVector3Tuple, PropRequired) &&
    validateOfType(data, "basePlaneNormal", isVector3Tuple, PropRequired) &&
    validatePrimitive(data, "baseArea", "number", PropRequired) &&
    validatePrimitive(data, "volume", "number", PropRequired) &&
    validatePrimitive(data, "heightMin", "number", PropRequired) &&
    validatePrimitive(data, "heightMax", "number", PropRequired)
  );
}
